import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { filter, first, map } from 'rxjs/operators';
import { AuthSessionStore } from '../+state';
import { navigateOrReport } from '../util/navigate-or-report.util';

/**
 * Route guard restricting activation to `supplier` users (or `admin`, who
 * may act on a supplier's behalf for support / verification work).
 *
 * Mirrors the persona constraint laid out in
 * `docs/product/05-roles-and-permissions.md` § Own organization & profile —
 * supplier-only surfaces (`/factory/new`, the supplier-facing factory
 * editor, etc.) must reject `client`-tier users. Buyer-side personas
 * (`buyer` / `government` / `zone`, all collapsed under the `client`
 * `UserType` until the enum split lands per `01-personas.md` Q1) get
 * redirected to `/`.
 *
 * Unauthenticated requests are expected to be handled upstream by
 * {@link authGuard}.
 *
 * @returns An {@link import('rxjs').Observable} resolving to `true` for
 *   `supplier` and `admin` users, `false` otherwise (with a redirect to `/`).
 * @example
 * import { Routes } from '@angular/router';
 * import { authGuard, supplierGuard } from '@indutrust/auth-data';
 *
 * export const routes: Routes = [
 *   {
 *     path: 'factories',
 *     canActivate: [authGuard, supplierGuard],
 *     loadChildren: async () =>
 *       import('@indutrust/factory-feature').then((m) => m.libFactoriesListRoutes),
 *   },
 * ];
 */
export const supplierGuard: CanActivateFn = () => {
  const authStore = inject(AuthSessionStore);
  const router = inject(Router);

  return toObservable(authStore.authenticationInitialized).pipe(
    filter((initialized): boolean => initialized),
    first(),
    map(() => {
      if (authStore.isSupplier() || authStore.isAdmin()) {
        return true;
      }
      navigateOrReport(router, ['/'], 'Navigation to home failed');
      return false;
    })
  );
};
